import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { DestinationWorld } from "@hangar/react-icons";
import {
  BreadcrumbContainer,
  BreadcrumbHeader,
  BreadcrumbNav,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbCurrent,
  BreadcrumbCurrentLabel,
  TitleSection,
  DescriptionSection
} from "./Breadcrumb.styles";
import { useTheme } from "styled-components";

export interface BreadcrumbItem {
  label: string;
  path?: string;
  icon?: React.ReactNode;
}

export interface BreadcrumbProps {
  title: string;
  description?: string | React.ReactNode;
  items?: BreadcrumbItem[];
  showHome?: boolean;
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  title,
  description,
  items = [],
  showHome = true
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    tokens: { color },
  } = useTheme();

  // Generate breadcrumb items based on current path if none provided
  const generateBreadcrumbItems = (): BreadcrumbItem[] => {
    if (items.length > 0) return items;
    
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbItems: BreadcrumbItem[] = [];
    
    pathSegments.forEach((segment, index) => {
      const path = '/' + pathSegments.slice(0, index + 1).join('/');
      const label = segment.charAt(0).toUpperCase() + segment.slice(1);
      
      breadcrumbItems.push({
        label,
        path: index === pathSegments.length - 1 ? undefined : path
      });
    });
    
    return breadcrumbItems;
  };

  const breadcrumbItems = generateBreadcrumbItems();

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  return (
    <BreadcrumbContainer>
      <BreadcrumbHeader>
        <BreadcrumbNav>
          {showHome && (
            <>
              <BreadcrumbItem>
                <BreadcrumbLink onClick={() => handleNavigation('/')}>
                  <DestinationWorld size={14} color={color.icon.secondary} />
                  <span>/</span>
                </BreadcrumbLink>
              </BreadcrumbItem>
              {breadcrumbItems.length > 0 && (
                <BreadcrumbSeparator>
                  ›
                </BreadcrumbSeparator>
              )}
            </>
          )}
          
          {breadcrumbItems.map((item, index) => (
            <React.Fragment key={index}>
              <BreadcrumbItem>
                {item.path ? (
                  <BreadcrumbLink onClick={() => handleNavigation(item.path!)}>
                    {item.icon}
                    <span>{item.label}</span>
                  </BreadcrumbLink>
                ) : (
                  <BreadcrumbCurrent>
                    {item.icon}
                    <BreadcrumbCurrentLabel>{item.label}</BreadcrumbCurrentLabel>
                  </BreadcrumbCurrent>
                )}
              </BreadcrumbItem>
              
              {index < breadcrumbItems.length - 1 && (
                <BreadcrumbSeparator>
                  ›
                </BreadcrumbSeparator>
              )}
            </React.Fragment>
          ))}
        </BreadcrumbNav>
        
        <TitleSection>
          <h1>{title}</h1>
        </TitleSection>
        
        {description && (
          <DescriptionSection>
            {typeof description === 'string' ? <p>{description}</p> : description}
          </DescriptionSection>
        )}
      </BreadcrumbHeader>
    </BreadcrumbContainer>
  );
};
